import Seo, { localBusinessSchema, breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import ConsultationForm from "../components/ConsultationForm";
import { useSettings } from "../lib/hooks";
import { telLink, waLink, track } from "../lib/api";
import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { useState } from "react";

export default function Contact() {
  const { data: settings } = useSettings();
  const [mapLoaded, setMapLoaded] = useState(false);

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact NOOR | Bristol, Bath & Somerset"
        description="Speak to NOOR Design & Build about your extension, renovation or conversion. Call, WhatsApp or request a consultation — free initial advice."
        path="/contact"
        schema={{ "@context": "https://schema.org", "@graph": [localBusinessSchema(settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])] }}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <Overline light>[ Contact ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
            Talk to us about your home.
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
            Call, WhatsApp or send a consultation request — whichever suits you. We respond to every genuine
            enquiry personally.
          </p>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-28 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-px">
            {[
              {
                icon: Phone, label: "Call NOOR", value: settings?.phone_display || "07782 760 279",
                href: telLink(settings), testid: "contact-call", event: "call_click",
              },
              {
                icon: MessageCircle, label: "WhatsApp", value: settings?.phone_display || "07782 760 279",
                href: waLink(settings), testid: "contact-whatsapp", event: "whatsapp_click", external: true,
              },
              {
                icon: Mail, label: "Email", value: settings?.email || "info@noordb.co.uk",
                href: `mailto:${settings?.email || "info@noordb.co.uk"}`, testid: "contact-email", event: "email_click",
              },
            ].map(({ icon: Icon, label, value, href, testid, event, external }) => (
              <Reveal key={label}>
                <a
                  href={href}
                  onClick={() => event && track(event, { location: "contact_page" })}
                  {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid={testid}
                  className="group flex items-center gap-5 border border-black/10 bg-cream hover:bg-stone p-6 transition-colors duration-300"
                >
                  <span className="w-11 h-11 border border-black/15 flex items-center justify-center shrink-0 group-hover:border-ember transition-colors duration-300">
                    <Icon size={17} strokeWidth={1.5} className="text-ember" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/50">{label}</span>
                    <span className="block font-display font-medium text-lg mt-0.5">{value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal>
              <div className="flex items-center gap-5 border border-black/10 p-6">
                <span className="w-11 h-11 border border-black/15 flex items-center justify-center shrink-0">
                  <MapPin size={17} strokeWidth={1.5} className="text-ember" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/50">Operating Area</span>
                  <span className="block font-display font-medium text-lg mt-0.5">{settings?.primary_area || "Bristol, Bath, Somerset and the surrounding areas"}</span>
                </span>
              </div>
            </Reveal>
            <Reveal>
              <div className="flex items-center gap-5 border border-black/10 p-6" data-testid="contact-registered-office">
                <span className="w-11 h-11 border border-black/15 flex items-center justify-center shrink-0">
                  <MapPin size={17} strokeWidth={1.5} className="text-ember" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/50">Registered Office</span>
                  <span className="block font-display font-medium text-lg mt-0.5">3rd Floor, Old Stock Exchange, St Nicholas Street, Bristol BS1 1TG</span>
                </span>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs text-ember mb-3">[ Request a Consultation ]</p>
              <h2 className="font-display font-light text-3xl tracking-tight mb-8">Tell us about your project.</h2>
              <ConsultationForm />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-stone border-t border-black/10">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <Overline>[ Find Us ]</Overline>
            <h2 className="font-display font-light text-3xl tracking-tight mt-3 mb-8">Based in the heart of Bristol.</h2>
            <div className="border border-black/10 p-2 bg-cream">
              <div className="relative">
                {/* Branded placeholder behind the iframe — the map fades in over it,
                    so visitors never stare at an empty box while Google Maps loads */}
                {!mapLoaded && (
                  <div
                    className="absolute inset-0 z-0 bg-stone flex flex-col items-center justify-center gap-3 blueprint-grid-light"
                    data-testid="contact-map-loading"
                    aria-hidden="true"
                  >
                    <MapPin size={28} strokeWidth={1.25} className="text-ember animate-pulse" />
                    <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-graphite/40">Loading map…</p>
                  </div>
                )}
                <iframe
                  title="NOOR Design & Build registered office — Old Stock Exchange, St Nicholas Street, Bristol"
                  src="https://www.google.com/maps?q=Old+Stock+Exchange,+St+Nicholas+Street,+Bristol+BS1+1TG&output=embed"
                  className={`relative z-10 w-full aspect-[16/9] md:aspect-[21/8] block transition-opacity duration-700 ${mapLoaded ? "opacity-100" : "opacity-0"}`}
                  loading="eager"
                  onLoad={() => setMapLoaded(true)}
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>
            </div>
            <p className="text-xs text-graphite/50 mt-4">Registered office — visits by appointment only. We carry out site visits across Bristol, Bath, Somerset and the surrounding areas.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
